import { Box, Button, Card, CardContent, Divider, Stack, Typography } from '@mui/material';

import { Offer } from '../../../../domain/qualification';
import { formatCentsCurrency } from '../../../../general/utils/formatters';

type Props = {
  offer: Offer;
  index: number;
  onSelect: (offerId: string) => void;
  hideFeeAndRebate?: boolean;
};

const calculateBillingPeriod = (billingCycle: number): string => {
  switch (billingCycle) {
    case 7:
      return 'Weekly';
    case 14:
      return 'Biweekly';
    case 30:
    default:
      return 'Monthly';
  }
};

const formatFuelRebate = (hundredthsOfCent: number): string => {
  const cents = hundredthsOfCent / 100;
  return `${cents.toFixed(2)}¢ Fuel`;
};

const formatNonFuelRebate = (basisPoints: number): string => {
  const percent = basisPoints / 100;
  return `${percent.toFixed(2)}% Non-fuel`;
};

type FieldProps = { label: string; value: string };

const Field = ({ label, value }: FieldProps) => (
  <Box>
    <Typography variant="body2" color="textSecondary">
      {label}
    </Typography>
    <Typography variant="subtitle1-medium">{value}</Typography>
  </Box>
);

const OfferCard = ({ offer, index, onSelect, hideFeeAndRebate = false }: Props) => {
  const rebate = `${formatFuelRebate(offer.fuelRebateHundredthsOfCent)} · ${formatNonFuelRebate(offer.nonFuelRebateBasisPoints)}`;

  return (
    <Card variant="outlined" sx={{ width: 303, flexShrink: 0, display: 'flex', borderRadius: 2 }}>
      <CardContent sx={{ display: 'flex', flexDirection: 'column', flex: 1, p: 3, '&:last-child': { pb: 3 } }}>
        <Typography variant="h5-bold" gutterBottom>
          Plan {index + 1}
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Stack spacing={2}>
          {!hideFeeAndRebate && <Field label="Rebate" value={rebate} />}
          {!hideFeeAndRebate && <Field label="Fee" value={`${formatCentsCurrency(offer.userFee)} User Fee`} />}
          <Field label="Payment due" value={calculateBillingPeriod(offer.billingCycle)} />
          <Field
            label="Paid within"
            value={`${offer.gracePeriodDays} ${offer.gracePeriodDays === 1 ? 'day' : 'days'} of statement end date`}
          />
        </Stack>
        <Box mt="auto" pt={4}>
          <Button variant="contained" color="primary" fullWidth onClick={() => onSelect(offer.id)}>
            Select plan
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export { OfferCard };
